import "./Renewals.scss";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Space, Button, DatePicker, notification, Tooltip } from "antd";
import dayjs from 'dayjs';
import { withLocale } from "../../l10n";
import service from "../../services/Service";
import { hasPrivilege } from "../../api/token";
import { downloadExcel } from "../../utils/excelexport";
import { STATE_ACTIVE, STATE_CANCELLED, STATE_EXPIRED, STATE_INACTIVE, STATE_NOT_APPLICABLE, KICONEX_UUID } from "../../utils/constants";
import DataTable from "../DataTable/DataTable";
import { GetColumnLiteralSearchProps } from "../../utils/functions";
import HistoryModal from "./HistoryModal";
import RenewalChart from "./RenewalChart";
import { tagAndNormalize, applyClientFilters, buildUpdatePayload } from "../../utils/renewalHelpers";
import EditRenewalModal from "./EditRenewalModal";
import ExportConfigModal from "./ExportConfigModal";
import RenewSubscriptionsModal from "./RenewSubscriptionsModal";
import { buildExportPackage } from "../../utils/renewalsExport";
import { formatDate } from '../../utils/dates';
import { useGetTenantsLookupQuery } from '../../services/lookupApi.js';

const { RangePicker } = DatePicker;


function Renewals ({ location, lang }) {

  // Estado principal
  const [renewals, setRenewals] = useState([]);
  const [allRenewals, setAllRenewals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [botonDisabled, setBotonDisabled] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Modales
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalRenew, setShowModalRenew] = useState(false);
  const [showModalHistory, setShowModalHistory] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Parametros
  const [dateRange, setDateRange] = useState([dayjs(), dayjs().add(1, 'month')]);
  const [filteredInfo, setFilteredInfo] = useState(() => {
    const initial = {
      renewal_diff: [STATE_ACTIVE, STATE_EXPIRED]
    };
    if (location?.state?.plan) {
      initial.subscription_type = [location.state.plan];
    }
    return initial;
  });

  const tableRef = useRef(null);
  const canEdit = hasPrivilege('renewal_edit');

  const { data: tenants = [] } = useGetTenantsLookupQuery();
  const tenantsFilter = useMemo(
    () => tenants.map(tenant => ({ text: tenant.name, value: tenant.uuid })),
    [tenants]
  );

  const getRowKey = useCallback(
    (record) => `${record.subscription_type}:${record.uuid}`,
    []
  );

  const Pfetch = useCallback(async () => {
    if (tenants.length === 0) return;

    try {
      setLoading(true);
      const types = ['m2m', 'plan'];
      const requests = types.map(type => {
        const endpoint = type === 'm2m'
          ? "m2m-subscriptions/renewals"
          : "plan-subscriptions/renewals";
        return service.get(endpoint).then(res => ({ res, type }));
      });

      const results = await Promise.all(requests);

      // Unimos y formateamos los datos
      let allRows = [];
      for (const { res, type } of results) {
        if (res?.status === 200 && res.data?.ok) {
          const tagged = tagAndNormalize(res.data.data || [], type).map(kibox => {
            const tenant = tenants.find(t => t.uuid === kibox.tenant_uuid);
            return {
              ...kibox,
              date_to_renew: formatDate(kibox.date_to_renew),
              renewal_diff:  Math.round((kibox.renewal_diff + Number.EPSILON) * 100) / 100,
              tenant_name:   (kibox.tenant_uuid && tenant) ? tenant.name : lang.handle("Sin organización"),
            };
          });
          allRows = allRows.concat(tagged);
        }
      }
      setAllRenewals(allRows);

    } catch (err) {
      console.log("Error: " + err);
    } finally {
      setLoading(false);
    }
  }, [tenants, lang]);

  // Adapta el rango de fechas segun la ruta de entrada.
  useEffect(() => {
    if (location?.state?.plan) {
      setDateRange(null);
    }
  }, [location?.state?.plan]);

  // Gestiona el cambio del listado visible de renovaciones
  useEffect(() => {
    setRenewals(applyClientFilters(allRenewals, { dateRange }));
  }, [allRenewals, dateRange]);

  // Ejecuta pfetch cada vez que este muta
  useEffect(() => {
    Pfetch();
  }, [Pfetch]);

  // Se encarga de mantener actualizada la ref de filteredInfo
  const filteredInfoRef = useRef(filteredInfo);
  useEffect(() => { 
    filteredInfoRef.current = filteredInfo; 
  }, [filteredInfo]);


  // CHECKBOX SELECTION
  const rowSelection = canEdit ? {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
      setBotonDisabled(rows.length === 0);
    }
  } : undefined;

  const handleTableChange = useCallback((pagination, filters) => {
    const newTypeFilter = filters.subscription_type;
    const prevTypeFilter = filteredInfoRef.current.subscription_type;

    if (JSON.stringify(newTypeFilter) !== JSON.stringify(prevTypeFilter)) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
      setBotonDisabled(true);
    }

    setFilteredInfo(filters);
  }, []);

  // MODAL EDIT
  const handleEditSubmit = useCallback(async (changedRows) => {
    if (changedRows.length === 0) return;

    try {
      setLoading(true);

      const currentType = changedRows[0].subscription_type;
      const payload = buildUpdatePayload(changedRows);
      const endpoint = currentType === 'm2m'
        ? 'm2m-subscriptions/updateAll'
        : 'plan-subscriptions/updateAll';

      const response = await service.put(endpoint, { payload });

      if (response?.status === 200 && response.data?.ok) {
        const updatedCount = response.data.data?.count || '';
        notification.success({
          message: `${updatedCount} ${lang.handle('suscripciones actualizadas correctamente')}`,
        });
        setSelectedRows([]);
        setSelectedRowKeys([]);
        setShowModalEdit(false);
        setBotonDisabled(true);
        await Pfetch();
      } else {
        notification.error({ message: lang.handle('Error en la operación') });
      }
    } catch {
      notification.error({ message: lang.handle('Error en la operación') });
    } finally {
      setLoading(false);
    }
  }, [lang, Pfetch]);

  // MODAL RENEW
  const handleRenewSubmit = useCallback(async (renewalData, subscriptionType, orderId) => {
    if (renewalData.length === 0) return;

    try {
      setLoading(true);
      const endpoint = subscriptionType === 'm2m'
        ? 'm2m-subscriptions/updateAll'
        : 'plan-subscriptions/updateAll';

      const response = await service.put(endpoint, { payload: renewalData, order_id: orderId });

      if (response?.status === 200 && response.data?.ok) {
        const renewedCount = response.data.data?.count || '';
        notification.success({
          message: `${renewedCount} ${lang.handle('suscripciones actualizadas correctamente')}`
        });
        setSelectedRows([]);
        setSelectedRowKeys([]);
        setShowModalRenew(false);
        setBotonDisabled(true);
        await Pfetch();
      } else {
        notification.error({ message: lang.handle('Error en la operación') });
      }
    } catch {
      notification.error({ message: lang.handle('Error en la operación') });
    } finally {
      setLoading(false);
    }
  }, [lang, Pfetch]);

  // COMMON MODAL HANDLER
  const handleCancel = useCallback(() => {
    setShowModalEdit(false);
    setShowModalRenew(false);
    setShowModalHistory(false);
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setBotonDisabled(true);
  }, []);

  const handlePeriodSelect = useCallback((period) => {
    const startDate = dayjs(period.from);
    const endDate = dayjs(period.to);
    
    setDateRange([startDate, endDate]);
    
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, []);

  // INFORMACIÓN DE LA GRÁFICA //
  const visibleTypes = useMemo(() =>
    (filteredInfo.subscription_type && filteredInfo.subscription_type.length > 0)
      ? filteredInfo.subscription_type
      : ['m2m', 'plan'],
    [filteredInfo.subscription_type]
  );

  const visibleTenants = useMemo(() =>
    (filteredInfo.tenant_name && filteredInfo.tenant_name.length > 0)
      ? filteredInfo.tenant_name
      : null,
    [filteredInfo.tenant_name]
  );
  
  const chartTitleSuffix = useMemo(() => {
    // Solo visible con un solo tenant
    if (visibleTenants && visibleTenants.length === 1) {
      const tenant = tenants.find(t => t.uuid === visibleTenants[0]);
      return tenant?.name || '';
    }
    return '';
  }, [visibleTenants, tenants]);


  // INFORMACIÓN DE LA LISTA //
  const columns = useMemo(() => {
    const activeTypes = (filteredInfo.subscription_type && filteredInfo.subscription_type.length > 0)
      ? filteredInfo.subscription_type
      : ['m2m', 'plan'];

    const cols = [
      {
        title: lang.handle('Tipo'),
        dataIndex: 'subscription_type',
        width: 80,
        filters: [
          { text: 'M2M', value: 'm2m' },
          { text: 'PLAN', value: 'plan' },
        ],
        filteredValue: filteredInfo.subscription_type || null,
        onFilter: (value, record) => record.subscription_type === value,
        render: (value) => value ? value.toUpperCase() : '',
      },
      {
        title: lang.handle('Pedido'),
        dataIndex: "order_id",
        ...GetColumnLiteralSearchProps("order_id", lang),
        filteredValue: filteredInfo.order_id || null,
      },
      {
        title: lang.handle('UUID'),
        dataIndex: "uuid",
        ...GetColumnLiteralSearchProps("uuid", lang),
        filteredValue: filteredInfo.uuid || null,
      },
      {
        title: lang.handle('Organización'),
        dataIndex: "tenant_name",
        filters: tenantsFilter,
        filteredValue: filteredInfo.tenant_name || null,
        onFilter: (value, record) => record.tenant_uuid === value,
      },
      {
        title: lang.handle('Próxima renovación'),
        dataIndex: "date_to_renew",
        defaultSortOrder: "ascend",
        sorter: (a, b) => dayjs(a.date_to_renew, 'DD-MM-YYYY').valueOf() - dayjs(b.date_to_renew, 'DD-MM-YYYY').valueOf(),
        sortDirections: ["descend", "ascend"],
      },
      {
        title: lang.handle('Estado'),
        dataIndex: "renewal_diff",
        filters: [
          { text: lang.handle("Caducado"), value: STATE_EXPIRED },
          { text: lang.handle("Lista para activación"), value: STATE_INACTIVE },
          { text: lang.handle("Activo"), value: STATE_ACTIVE },
          { text: lang.handle("Cancelado"), value: STATE_CANCELLED },
          { text: lang.handle("No procede"), value: STATE_NOT_APPLICABLE },
        ],
        filteredValue: filteredInfo.renewal_diff || null,
        onFilter: (value, record) =>
          record.state && record.state.toLowerCase() === value.toLowerCase(),
        render: function (data, record) {
          let label;
          switch (record.state) {
            case STATE_EXPIRED:
              label = lang.handle("Caducado");
              break;
            case STATE_INACTIVE:
              label = lang.handle('Lista para activación');
              break;
            case STATE_ACTIVE:
              label = lang.handle('Activo');
              break;
            case STATE_CANCELLED:
              label = lang.handle('Cancelado');
              break;
            case STATE_NOT_APPLICABLE:
              label = lang.handle('No procede');
              break;
            default:
              label = 'error';
              break;
          }
          if (data === null) {
            return null;
          } else {
            if (record.state === STATE_ACTIVE) {
              return data < 0 ? <p> {lang.handle('Caducada')} </p> : <p> {lang.handle('Activa')} - {Math.floor(data) + lang.handle(" días restantes")} </p>;
            } else {
              return <p>{label}</p>
            }
          }
        },
      },
    ];

    if (activeTypes.includes('m2m')) {
      cols.splice(3, 0, {
        title: 'ICC',
        dataIndex: "icc",
        ...GetColumnLiteralSearchProps("icc", lang),
        filteredValue: filteredInfo.icc || null,
      });
    }
    if (activeTypes.includes('plan')) {
      const orgIndex = cols.findIndex(c => c.dataIndex === 'tenant_name');
      cols.splice(orgIndex + 1, 0, {
        title: lang.handle('Plan contratado'),
        dataIndex: "ki_subscription_name",
      });
    }

    return cols;
  }, [lang, filteredInfo, tenantsFilter]);

  // Refleja exactamente lo que la tabla muestra.
  const currentTableData = useMemo(() => {
    let data = renewals;
    for (const col of columns) {
      const key = col.key || col.dataIndex;
      const values = filteredInfo[key];
      if (!values || values.length === 0) continue;
      if (!col.onFilter) continue;
      data = data.filter(row => values.some(v => col.onFilter(v, row)));
    }
    return data;
  }, [renewals, columns, filteredInfo]);


  // EXCEL EXPORT //

  /**
   * Recopila la informacion de la tabla y mapas necesarios para construir el excel
   */
  const getExportData = useCallback(async () => {
    // Mapeo de nombre de instalaciones
    const devicesRes = await service.get('devices');
    const devices = devicesRes?.data?.ok ? devicesRes.data.data : [];
    const deviceNameMap = new Map(devices.map(d => [d.device_uuid, d.name]));

    // Mapeo de nombre de tarifas (solo para m2m)
    let poolNameMap = new Map();
    const hasM2M = currentTableData.some(r => r.subscription_type === 'm2m');
    if (hasM2M) {
      const m2mRes = await service.get('m2m', { tenant_uuid: KICONEX_UUID });
      poolNameMap = new Map((m2mRes?.data || []).map(sim => [sim.icc, sim.pool_name]));
    }

    return { data: currentTableData, maps: { deviceNameMap, poolNameMap } };

  }, [currentTableData]);

  const [startDate, endDate] = dateRange || [null, null];
  const from = startDate ? startDate.format('YYYY-MM-DD') : '';
  const to = endDate ? endDate.format('YYYY-MM-DD') : '';

  /**
   *  Gestiona la configuracion y lanza la descarga del excel al confirmar en el modal
   */ 
  const handleExportConfirm = useCallback(async (config) => {
    setShowExportModal(false);
    const { data, maps } = await getExportData();
    const { fileName, sheetsData } = buildExportPackage(  
      data, config.extraColumns ?? [], from, to, maps
    );

    await downloadExcel(sheetsData, fileName);

  }, [getExportData, from, to]);

  // PRESETS DE RANGO DE FECHAS PARA EL FILTRO
  const rangePresets = useMemo(() => [
    {
      label: lang.handle('Próximo mes'),
      value: [dayjs().add(1, 'month').startOf('month'), dayjs().add(1, 'month').endOf('month')],
    },
    {
      label: lang.handle('Próximos 3 meses'),
      value: [dayjs().add(1, 'month').startOf('month'), dayjs().add(3, 'month').endOf('month')],
    },
    {
      label: lang.handle('Próximos 6 meses'),
      value: [dayjs().add(1, 'month').startOf('month'), dayjs().add(6, 'month').endOf('month')],
    },
    {
      label: lang.handle('Próximos 12 meses'),
      value: [dayjs().add(1, 'month').startOf('month'), dayjs().add(12, 'month').endOf('month')],
    },
    {
      label: lang.handle('Próximo año'),
      value: [dayjs().add(1, 'year').startOf('year'), dayjs().add(1, 'year').endOf('year')],
    },
  ], [lang]);

  // CONDICIONES PARA HABILITAR BOTONES DE ACCIÓN
  const allSameType = selectedRows.length === 0
    ? true
    : selectedRows.every(row => row.subscription_type === selectedRows[0].subscription_type);

  const mixedTypesTooltip = (!botonDisabled && !allSameType)
    ? lang.handle('Para editar o renovar selecciona suscripciones del mismo tipo')
    : '';

  const canRenew = useMemo(() =>
    selectedRows.every(row =>
      row.subscription_state === STATE_ACTIVE ||
      row.subscription_state === STATE_CANCELLED
    ),
    [selectedRows]
  );

  return (
    <div>
      {canEdit && (
        <RenewalChart
          data={allRenewals}
          types={visibleTypes}
          tenantUuids={visibleTenants}
          titleSuffix={chartTitleSuffix}
          onPeriodSelect={handlePeriodSelect}
        />
      )}
      <div className="d-flex justify-content-center mb-4">
        <RangePicker
          value={dateRange}
          presets={rangePresets}
          format="YYYY-MM-DD"
          size="large"
          onChange={(dates) => setDateRange(dates || null)}
        />
      </div>
      <div ref={tableRef}>
        <DataTable
          columns={columns}
          rowKey={getRowKey}
          dataSource={renewals}
          loading={loading}
          rowSelection={rowSelection}
          onChange={handleTableChange}
        />
        <div className="button-panel mt-4">
          <Space>
            {canEdit && (
              <>
                <Tooltip title={mixedTypesTooltip}>
                  <Button
                    type="primary"
                    onClick={() => setShowModalEdit(true)}
                    disabled={botonDisabled || !allSameType}
                  >
                    {lang.handle('Editar')}
                  </Button>
                </Tooltip>
                <Tooltip title={mixedTypesTooltip}>
                  <Button
                    type="primary"
                    onClick={() => setShowModalRenew(true)}
                    disabled={botonDisabled || !allSameType || !canRenew}
                  >
                    {lang.handle('Renovar')}
                  </Button>
                </Tooltip>
                <Button
                  onClick={() => setShowModalHistory(true)}
                  disabled={selectedRows.length !== 1}
                >
                  {lang.handle('Histórico')}
                </Button>
              </>
          )}
            <Button onClick={() => setShowExportModal(true)}>
              {lang.handle('Descargar Informe')}
            </Button>
          </Space>
        </div>
      </div>

      {/* MODAL DE EDICION */}
        <EditRenewalModal
          open={showModalEdit}
          loading={loading}
          rows={selectedRows}
          onSubmit={handleEditSubmit}
          onCancel={handleCancel}
          getRowKey={getRowKey}
        />

      {/* MODAL DE RENOVACION */}
      <RenewSubscriptionsModal
        open={showModalRenew}
        loading={loading}
        rows={selectedRows}
        onSubmit={handleRenewSubmit}
        onCancel={handleCancel}
        getRowKey={getRowKey}
      />

      {/* MODAL DE HISTORICO */}
      <HistoryModal
        open={showModalHistory}
        selectedRow={selectedRows[0]}
        subscriptionType={selectedRows[0]?.subscription_type}
        onClose={handleCancel}
      />

      {/* MODAL DE EXPORTACIÓN */}
      <ExportConfigModal
        open={showExportModal}
        onCancel={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
      />
    </div>
  );
}

export default withLocale(Renewals);
